import { AnalysisServiceModel } from "e.sic-platform.webclient/src/services/analysis/AnalysisService";
import { api } from "e.sic-platform.webclient/src/services/api/ApiService";
import { AppStorageModel } from "e.sic-platform.webclient/src/services/app/AppStorage";
import { ConversionServiceModel } from "e.sic-platform.webclient/src/services/conversion/VideoConversionService";
import { errorHandler } from "e.sic-platform.webclient/src/services/error-handler/ErrorHandlerService";
import { InspectionReviewServiceModel } from "e.sic-platform.webclient/src/services/ocr/InspectionReviewService";
import { OcrServiceModel } from "e.sic-platform.webclient/src/services/ocr/OcrService";
import { RestoreDataServiceModel } from "e.sic-platform.webclient/src/services/restore-data/RestoreDataService";
import { SegmentationServiceModel } from "e.sic-platform.webclient/src/services/segmentation/SegmentationService";
import { ActivityTypes } from "e.sic-platform.webclient/src/services/shop/ActivityPricesStorage";
import { FileStates } from "e.sic-platform.webclient/src/services/shop/ShopHistoryStorage";
import { creditBalanceStorage } from "e.sic-platform.webclient/src/services/shop/CreditBalanceStorage";
import Language from "e.sic-platform.webclient/src/services/translation/language";
import { uploadFilesService } from "e.sic-platform.webclient/src/services/upload/UploadFilesService";

export const segmentationApp = AppStorageModel.create(
    {
        services: {videoSegmentation: SegmentationServiceModel.create({activityType: ActivityTypes.HighlightDetection, language: Language.english})},
        route: "/video-segmentation",
        activityType: [ActivityTypes.HighlightDetection],
        descriptionkey: "videoSegmentation.description",
        image: "/images/video-segmentation.png",
        titleKey: "general.apps.video-segmentation.title"
    },
    {
        api: api,
        uploadFilesService: uploadFilesService,
        errorHandler: errorHandler,
        creditBalanceStorage: creditBalanceStorage
    }
)

export const analysisApp = AppStorageModel.create(
    {
        services: {videoAnalysis: AnalysisServiceModel.create({activityType: ActivityTypes.VideoAnalysis, language: Language.english})},
        route: "/video-analysis",
        activityType: [ActivityTypes.VideoAnalysis],
        descriptionkey: "videoAnalysis.description",
        image: "/images/video-analysis.png",
        titleKey: "general.apps.video-analysis.title"
    },
    {
        api: api,
        uploadFilesService: uploadFilesService,
        errorHandler: errorHandler,
        creditBalanceStorage: creditBalanceStorage
    }
)

export const conversionApp = AppStorageModel.create(
    {
        services: {videoConversion: ConversionServiceModel.create({activityType: ActivityTypes.VideoConversion})},
        route: "/video-conversion",
        activityType: [ActivityTypes.VideoConversion],
        descriptionkey: "videoConversion.description",
        image: "/images/video-conversion.png",
        titleKey: "general.apps.video-conversion.title"
    }, 
    {
        api: api,
        uploadFilesService: uploadFilesService,
        creditBalanceStorage: creditBalanceStorage
    }
);

export const ocrApp = AppStorageModel.create(
    {
        // status starts at None, not the model default (Running): with no file open there is
        // no run, and the header's Order & process panel lists a service the moment it reports
        // one -- so the default made a first visit to the page open on a "Running" order for a
        // file that does not exist. The two standalone services below start the same way.
        services: {ocrService: OcrServiceModel.create({activityType: ActivityTypes.VideoOcr, status: FileStates.None})},
        route: "/inspection-insights",
        activityType: [ActivityTypes.VideoOcr],
        // The Inspection Insights page is the single "app" for running any combination of
        // OCR, Analysis and Segmentation, so its usage list must show every combination a
        // cycle can produce — not just OCR-only. Matching is by EXACT activity set
        // (ShopProvider.IsServiceRequested), so each combination is listed explicitly.
        // NOTE: the Analysis-only / Analysis+Segmentation sets also match files run from
        // the standalone Video Analysis / Segmentation apps (same activity set) — that
        // overlap is intentional so all of the user's transactions surface here.
        historyServiceSets: [
            [ActivityTypes.VideoOcr],
            [ActivityTypes.VideoOcr, ActivityTypes.HighlightDetection],
            [ActivityTypes.VideoOcr, ActivityTypes.VideoAnalysis],
            [ActivityTypes.VideoOcr, ActivityTypes.VideoAnalysis, ActivityTypes.HighlightDetection],
            [ActivityTypes.VideoAnalysis],
            [ActivityTypes.VideoAnalysis, ActivityTypes.HighlightDetection],
        ],
        descriptionkey: "ocrService.description",
        image: "/images/ocr-service.png",
        titleKey: "general.apps.ocr-service.title"
    },
    {
        api: api,
        uploadFilesService: uploadFilesService,
        creditBalanceStorage: creditBalanceStorage
    }
)

// Standalone Video Analysis service backing the "Video Analysis" tab on the
// Inspection Insights page. It is intentionally NOT added to ocrApp.services:
// AppStorage.runAfterUpload() iterates every service and AnalysisService's
// runAfterUpload() calls startService() — so wiring it into ocrApp would
// auto-start a (paid) video analysis on every Inspection Insights upload.
// Instead this instance loads the same file's VA data on demand (fileId synced
// from the OCR service) and only runs analysis when the user asks for it.
export const inspectionVideoAnalysis = AnalysisServiceModel.create(
    { activityType: ActivityTypes.VideoAnalysis, language: Language.english, alwaysUseVaData: true, status: FileStates.None },
    {
        api: api,
        uploadFilesService: uploadFilesService,
        errorHandler: errorHandler,
        creditBalanceStorage: creditBalanceStorage
    }
)

// The human sign-off on the Inspection Insights results. Standalone for the same reason as
// the two services above — and additionally because it applies to every kind of run: an
// analysis-only file has no OCR data but is still reviewed, approved, and exported.
export const inspectionReview = InspectionReviewServiceModel.create(
    {},
    {
        api: api,
        errorHandler: errorHandler
    }
)

// Standalone Segmentation service backing the optional "Mark damage" overlay on the
// Inspection Insights page. Like inspectionVideoAnalysis it is intentionally NOT in
// ocrApp.services so uploading to Inspection Insights never auto-runs a (paid)
// segmentation. It loads the same file's segmentation masks on demand (fileId synced
// from the OCR service) so the user can overlay damage marks on the video + frames.
export const inspectionSegmentation = SegmentationServiceModel.create(
    { activityType: ActivityTypes.HighlightDetection, language: Language.english, alwaysUseVaData: true, status: FileStates.None },
    {
        api: api,
        uploadFilesService: uploadFilesService,
        errorHandler: errorHandler,
        creditBalanceStorage: creditBalanceStorage
    }
)

export const analysisAndSegmentationApp = AppStorageModel.create(
    {
        services: {
            videoAnalysis: AnalysisServiceModel.create({activityType: ActivityTypes.VideoAnalysis, language: Language.english}),
            videoSegmentation: SegmentationServiceModel.create({activityType: ActivityTypes.HighlightDetection, language: Language.english})
        },
        route: "/analysis-and-segmentation",
        activityType: [ActivityTypes.VideoAnalysis, ActivityTypes.HighlightDetection],
        descriptionkey: "analysis-and-segmentation.description",
        titleKey: "general.apps.analysis-and-segmentation.title",
        image: "/images/analysis-and-segmentation.png"
    },
    {
        api: api,
        uploadFilesService: uploadFilesService,
        errorHandler: errorHandler,
        creditBalanceStorage: creditBalanceStorage
    }
)

export const analysisAndOcrApp = AppStorageModel.create(
    {
        services: {
            videoAnalysis: AnalysisServiceModel.create({activityType: ActivityTypes.VideoAnalysis, language: Language.english}),
            ocrService: OcrServiceModel.create({activityType: ActivityTypes.VideoOcr})
        },
        route: "/analysis-and-inspection-insights",
        activityType: [ActivityTypes.VideoAnalysis, ActivityTypes.VideoOcr],
        descriptionkey: "analysis-and-ocr.description",
        titleKey: "general.apps.analysis-and-ocr.title",
        image: "/images/analysis-and-ocr.png"
    },
    {
        api: api,
        uploadFilesService: uploadFilesService,
        errorHandler: errorHandler,
        creditBalanceStorage: creditBalanceStorage
    }
)

export const restoreDataApp = AppStorageModel.create(
    {
        services: {restoreDataService: RestoreDataServiceModel.create({activityType: ActivityTypes.VideoOcr})},
        route: "/restore-esic-data",
        activityType: [ActivityTypes.VideoOcr],
        descriptionkey: "restoreEsicData.description",
        image: "/images/restore_data.jpg",
        titleKey: "general.apps.restore-esic-data.title",
        uploadAllFileTypes: true
    },
    {
        api: api,
        uploadFilesService: uploadFilesService,
        creditBalanceStorage: creditBalanceStorage
    }
)